import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchServicePreferences } from '@/hooksApi';
import ServiceIcon, { hasServiceGlyph } from '@/components/OrderForm/ServiceIcon';
import { SectionHeader } from './SectionHeader';
import useUserStore, { selectUsesServices } from '@/stores/userStore';

// NOTE: Est. Delivery date is not available from the current API response.
// A `estimated_delivery_at` field will be needed on the Carrier/Order response to populate this row.

interface CarrierSectionProps {
  /**
   * The service level THIS order shipped on (`orders.service_code`).
   *
   * ⚠️ Not the same axis as `carrier_selection_mode`, which
   * says WHO picks the carrier; `service_code` says WHICH service the merchant
   * bought. Both happen to have a `boxy_select` value, and reading the mode as
   * if it were the service is exactly the bug this prop exists to fix: a
   * merchant on Boxy Save was told "Boxy picks the best delivery option for
   * every order".
   */
  serviceCode?: string | null;
}

export function CarrierSection({ serviceCode }: CarrierSectionProps) {
  const { t } = useTranslation();
  const { data: servicePreferences } = useFetchServicePreferences();
  const usesServices = useUserStore(selectUsesServices);

  // The catalogue owns the merchant-facing wording, so the name and blurb here
  // are the same strings the settings and create-order screens show. A code the
  // catalogue does not contain resolves to undefined and falls back — an order
  // predating the catalogue, or a service since retired, must not blank the card.
  const service = servicePreferences?.services?.find((item) => item.code === serviceCode);

  const serviceName = service?.title ?? String(t('boxy_select_service_name'));
  const serviceBlurb = service?.description ?? String(t('boxy_select_service_subtitle'));

  const serviceRow = (
    <div className='flex items-center gap-3 pb-2'>
      {/* The service's OWN mark, the same one the create-order step and the
          settings list show. It used to be a generic truck here, so the same
          service looked like one thing while choosing it and another while
          reviewing the order it shipped on — the icon stopped identifying the
          service and became decoration.

          The truck stays as the fallback, not as the default: an order
          predating the catalogue, or one on a service with no bundled glyph,
          still needs something in the tile. */}
      <div className='h-10 w-10 rounded-xl border border-sd-border flex items-center justify-center shrink-0'>
        {hasServiceGlyph(serviceCode) ? (
          <ServiceIcon code={serviceCode as string} className='h-5 w-5 text-sd-foreground' />
        ) : (
          <Truck className='h-5 w-5 text-sd-muted-foreground' strokeWidth={1.5} />
        )}
      </div>
      <div className='flex flex-col'>
        <span className='text-sm font-semibold text-sd-foreground'>{serviceName}</span>
        <span className='text-xs text-sd-muted-foreground'>{serviceBlurb}</span>
      </div>
    </div>
  );

  // The merchant ships on a SERVICE, never a named carrier (owner 2026-10-28:
  // "we don't show carriers in dashboard, never ever"). Nothing about the
  // real carrier is read here, so it cannot reach the DOM even when the API
  // still sends it. A merchant with no plan (uses_services=false) has no
  // service either, so there is no card at all.
  if (!usesServices) return null;
  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={Truck} title={String(t('boxy_select_service_title'))} />
      {serviceRow}
    </div>
  );
}
